/**
 * Naming engine — verbatim port of the monolith's build() (index.html L4444)
 * and its helpers (monAbbr, initNum, sendTypeFor L4437–4443, initCampaign
 * L4181, STAGE tags L4160, PERSONA terms L4161).
 *
 * Real Lennar SFMC email-name convention (reverse-engineered from 348 live
 * template names):
 *   {DIV}LEN _ M _ {AUD} _ {Market·Brand·Comm} _ {YY}{Stage}{Mon}{Init#}Tmp _
 *   {YYYYMMDD} _ {SendType} _ {Amount} _ {Flag}
 * 9 segments. seg2 channel is always "M" (Marketing). seg3 audience ∈
 * LO/L/O/LOR/R. seg7 send-type ∈ SEvt/Inctve/Tour/FTHB/BrCom/55/GenOff.
 */
import { stageFromMsg, personaKeyFromUtm } from "./audience";
import type { UtmParts } from "./submit";

/* monolith STAGE — build() reads only .tag */
const STAGE_TAG: Record<string, string> = { Tease: "Tsr", Launch: "Lch", Sustain: "Sus", Urgency: "Lst", LastChance: "Lst", Events: "CATEG" };
/* monolith PERSONA[..].term */
const PERSONA_TERM: Record<string, string> = { FTHB: "FTHB", MoveUp: "MU", ActiveAdult: "AA", Investor: "Inv", Community: "Community", Market: "Market", National: "National" };

const monAbbr = (mm: string) => ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][parseInt(mm, 10)] || "Jan";
const initNum = (l: string) => (String(l || "").match(/\b(\d{3,5})\b/) || [])[1] || "";

export function initCampaign(l: string): string {
  l = l || "";
  if (/National Sales Event/i.test(l)) return "NSE";
  if (/National Campaign/i.test(l)) return "NationalCampaign";
  if (/Grand Opening/i.test(l)) return "GrandOpening";
  if (/Coming Soon/i.test(l)) return "ComingSoon";
  if (/Evergreen/i.test(l)) return "Evergreen";
  if (/Weekly Offers/i.test(l)) return "WeeklyOffers";
  if (/Broker Outreach/i.test(l)) return "BrokerOutreach";
  if (/Specific Focus/i.test(l)) return "SpecificFocus";
  if (/Last Chance/i.test(l)) return "LastChance";
  if (/Event/i.test(l)) return "Event";
  return "Campaign";
}

function sendTypeFor(stage: string, aud: string, pk: string): string {
  if (aud === "R" || aud === "LOR") return "BrCom";
  if (pk === "ActiveAdult") return "55";
  if (stage === "Urgency") return "Inctve";
  if (stage === "Tease") return "Tour";
  return "SEvt";
}

export interface NamingInput {
  divisionCode: string;
  /** first market chip */
  market?: string;
  /** first community (raw name) */
  community?: string;
  /** Primary Message CODE */
  messageCode?: string;
  initiative?: string;
  /** deploy date YYYY-MM-DD */
  deployDate?: string;
  /** Email Audience Type code */
  audType?: string;
  /** UTM Targeting value (persona key source) */
  utmTarget?: string;
}

export interface NamingResult {
  segs: { v: string; d: string }[];
  name: string;
  utm: UtmParts;
  stage: string;
  aud: string;
  geo: string;
  sendType: string;
  url: string;
}

/** monolith build() — the email name + UTM 2026 parts + tracked URL */
export function buildNaming(i: NamingInput): NamingResult {
  const div = i.divisionCode || "";
  const code = div + "LEN";
  /* NO SILENT "ATL" (2026-08-19, deliberate divergence from the monolith's build(),
     index.html:4675). The monolith could hardcode this fallback because its form seeded
     market=["ATL"], so the fallback was unreachable in practice. The repo now opens BLANK
     per Jack, which makes it reachable — and a UTM that quietly claims Atlanta for a send
     with no market chosen is worse than one that's visibly incomplete. Market is required
     before submit, so this only ever shows in the pre-submit preview. */
  const mkt = i.market || "";
  const commClean = i.community ? String(i.community).replace(/[^A-Za-z0-9]/g, "") : "";
  const geo = commClean || mkt || "MARKET.BRAND.COMM";
  const aud = i.audType || "LO";
  const stage = stageFromMsg(i.messageCode || "");
  const tag = STAGE_TAG[stage] || "Sus";
  const pk = personaKeyFromUtm(i.utmTarget);
  const term = PERSONA_TERM[pk] || "Market";
  const msg = i.messageCode || "";
  const initCamp = initCampaign(i.initiative || "");
  const inum = initNum(i.initiative || "");
  const date = (i.deployDate || "2026-04-18").replace(/-/g, "");
  const tmpTag = `${date.substring(2, 4)}${tag}${monAbbr(date.substring(4, 6))}${inum || "001"}Tmp`;
  const sendType = sendTypeFor(stage, aud, pk);
  const amount = "NA", flag = "N";
  const segs = [
    { v: code, d: "Division" }, { v: "M", d: "Channel · Marketing" }, { v: aud, d: "Audience" },
    { v: geo, d: "Market · Brand · Community" }, { v: tmpTag, d: "Campaign / Template" },
    { v: date, d: "Deploy date" }, { v: sendType, d: "Send type" }, { v: amount, d: "Offer amount" }, { v: flag, d: "National flag" },
  ];
  const name = segs.map((s) => s.v).join("_");
  const utm: UtmParts = {
    source: "mc", medium: "email",
    campaign: `${code}_${inum || "017"}-${initCamp}_${term}_${mkt}`,
    term: `${term}_${msg}`,
    content: "hero_CTA",
    adgroupname: name,
  };
  return {
    segs, name, utm, stage, aud, geo, sendType,
    url: `https://buy.lennar.com/?utm_source=mc&utm_medium=email&utm_campaign=${utm.campaign}&utm_term=${utm.term}&utm_content=${utm.content}&utm_adgroupname=${utm.adgroupname}`,
  };
}
